// components/Purchase/OrderSummaryModal.jsx

import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useVideo } from "../../context/VideoContext";
import { useNotification } from "../../context/NotificationContext";
import {
  createOrderApi,
  requestMellatPaymentApi,
} from "../../services/paymentApi";
import {
  toPersianDate,
  toPersianDateTime,
  normalizePhoneNumber,
} from "../../utils/dateUtils";
import {
  checkResponseData,
  checkErrorMessage,
} from "../../utils/responseHandler";
import { getPortalContainer } from "../../utils/portalContainer";
import "./OrderSummaryModal.css";

const OrderSummaryModal = ({
  isOpen,
  onClose,
  onBack,
  selectedPackage,
  discountData,
  userInfo,
  onPaymentComplete,
}) => {
  const {
    isFullscreen: isVideoFullscreen,
    isIOS,
    videoMetadata,
    currentTime,
    videoFile,
  } = useVideo();

  const { showNotification } = useNotification();

  const [loading, setLoading] = useState(false);
  const [orderInfo, setOrderInfo] = useState(null);
  const [error, setError] = useState("");
  const [redirecting, setRedirecting] = useState(false);
  const [refId, setRefId] = useState("");

  // ایجاد سفارش هنگام باز شدن مودال
  useEffect(() => {
    if (isOpen && selectedPackage && discountData) {
      createOrder();
    }
  }, [isOpen]);

  // ایجاد سفارش
  const createOrder = async () => {
    try {
      setLoading(true);
      setError("");

      const orderData = {
        discountCodeToken: "",
        productId: selectedPackage.packageId._id,
        referralCodeToken: discountData.discountInfo?.codeToken || "",
      };

      const response = await createOrderApi(orderData);
      const result = checkResponseData({ data: response });

      if (result.success && response.data) {
        setOrderInfo(response.data);
      } else {
        setError(response?.message || "خطا در ایجاد سفارش");
      }
    } catch (err) {
      const errorDetails = checkErrorMessage(err);
      setError(errorDetails.message || "خطا در ایجاد سفارش");
    } finally {
      setLoading(false);
    }
  };

  // ذخیره اطلاعات فیلم در localStorage
  const saveMovieInfo = () => {
    try {
      // اگه فیلمی در حال پخش نیست، ذخیره نکن
      if (!videoMetadata?.fileName && !videoFile) {
        return;
      }

      const movieInfo = {
        fileName: videoFile?.name || videoMetadata?.fileName || "",
        filmName: videoMetadata?.filmName || "",
        subtitle1Language: localStorage.getItem("subtitle1Language") || "",
        subtitle2Language: localStorage.getItem("subtitle2Language") || "",
        subtitle1Id: localStorage.getItem("subtitle1Id") || "",
        subtitle2Id: localStorage.getItem("subtitle2Id") || "",
        subtitle1File: localStorage.getItem("subtitle1File") || "",
        subtitle2File: localStorage.getItem("subtitle2File") || "",
        currentTime: currentTime || 0,
        timestamp: new Date().toISOString(),
        // اینا رو هم اضافه کنیم بد نیست:
        videoType: videoMetadata?.videoType || "", // movie یا series
        season: videoMetadata?.season || "",
        episode: videoMetadata?.episode || "",
      };

      localStorage.setItem("movieInfoBeforPay", JSON.stringify(movieInfo));
      console.log("اطلاعات فیلم ذخیره شد:", movieInfo);
    } catch (error) {
      console.error("خطا در ذخیره اطلاعات فیلم:", error);
    }
  };

  // رفتن به درگاه پرداخت
  const handlePayment = async () => {
    if (!orderInfo) return;

    try {
      setRedirecting(true);
      showNotification({
        message: "در حال اتصال به درگاه بانک...",
        type: "info",
        duration: 3000,
      });

      // ذخیره اطلاعات فیلم
      saveMovieInfo();

      // آماده‌سازی شماره موبایل
      let mobileNo = "";
      if (userInfo?.phoneNumber) {
        mobileNo = normalizePhoneNumber(userInfo.phoneNumber);
      }

      const paymentData = {
        gateway: "mellatGateway",
        mobileNo: mobileNo || "",
        orderId: orderInfo.orderId,
      };

      const response = await requestMellatPaymentApi(paymentData);

      if (response?.success && response?.RefId) {
        setRefId(response.RefId);

        // نمایش پیام و انتقال به درگاه
        showNotification({
          message: response.message || "در حال انتقال به درگاه بانک...",
          type: "success",
          duration: 2000,
        });

        // انتقال به درگاه بعد از 500ms
        setTimeout(() => {
          const form = document.getElementById("formBankMellat");
          if (form) {
            form.submit();
          }
        }, 500);
      } else {
        // در صورت خطا، اطلاعات فیلم رو پاک کن
        localStorage.removeItem("movieInfoBeforPay");
        setError(response?.message || "خطا در اتصال به درگاه بانک");
        setRedirecting(false);
      }
    } catch (err) {
      localStorage.removeItem("movieInfoBeforPay");
      const errorDetails = checkErrorMessage(err);
      setError(errorDetails.message || "خطا در اتصال به درگاه بانک");
      setRedirecting(false);
    }
  };

  const formatPrice = (price) => {
    const toman = price / 10;
    return toman.toLocaleString() + " تومان";
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="order-modal-overlay">
      <div className="order-modal" onClick={(e) => e.stopPropagation()}>
        <div className="order-modal-header">
          <button
            className="back-btn"
            onClick={onBack}
            disabled={loading || redirecting}
          >
            ← بازگشت
          </button>
          <h3>خلاصه سفارش</h3>
          <button
            className="close-btn"
            onClick={onClose}
            disabled={loading || redirecting}
          >
            ✕
          </button>
        </div>

        <div className="order-modal-content">
          {loading && !orderInfo && (
            <div className="order-loading">
              <div className="spinner"></div>
              <p>در حال ایجاد سفارش...</p>
            </div>
          )}

          {error && (
            <div className="order-error">
              <p>{error}</p>
              <button onClick={createOrder} className="retry-btn">
                تلاش مجدد
              </button>
            </div>
          )}

          {orderInfo && (
            <>
              {/* اطلاعات سفارش */}
              <div className="order-details">
                <div className="detail-row">
                  <span>محصول:</span>
                  <span className="detail-value">
                    {selectedPackage.packageId.displayName}
                  </span>
                </div>

                <div className="detail-row">
                  <span>شماره سفارش:</span>
                  <span className="detail-value order-id">
                    {orderInfo.orderId}
                  </span>
                </div>

                <div className="detail-row">
                  <span>تاریخ شروع:</span>
                  <span className="detail-value">
                    {toPersianDate(orderInfo.startingDate)}
                  </span>
                </div>

                <div className="detail-row">
                  <span>تاریخ پایان:</span>
                  <span className="detail-value">
                    {toPersianDate(orderInfo.endingDate)}
                  </span>
                </div>

                <div className="detail-row total">
                  <span>مبلغ قابل پرداخت:</span>
                  <span className="final-price">
                    {formatPrice(orderInfo.payedPrice)}
                  </span>
                </div>
              </div>

              {/* انتخاب درگاه */}
              <div className="gateway-section">
                <h4>انتخاب درگاه پرداخت</h4>
                <div className="gateway-card">
                  <div className="gateway-info">
                    <img
                      src="/mellat-logo.png"
                      alt="بانک ملت"
                      className="gateway-logo"
                    />
                    <span>درگاه بانک ملت</span>
                  </div>
                  <button
                    className="pay-btn"
                    onClick={handlePayment}
                    disabled={redirecting}
                  >
                    {redirecting ? "در حال انتقال..." : "پرداخت"}
                  </button>
                </div>
              </div>

              {/* فرم پنهان بانک ملت */}
              <form
                style={{ display: "none" }}
                method="post"
                name="formBankMellat"
                id="formBankMellat"
                action="https://bpm.shaparak.ir/pgwchannel/startpay.mellat"
              >
                <input type="hidden" value={refId} id="RefId" name="RefId" />
                <input type="submit" />
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const portalContainer = getPortalContainer(isIOS, isVideoFullscreen);
  return ReactDOM.createPortal(modalContent, portalContainer);
};

export default OrderSummaryModal;
